
import { useState, useEffect } from 'react';
import { Plus, Search, Users, X, Filter, AlertTriangle } from 'lucide-react';
import { useCreators } from '@/hooks/useCreators';
import CreatorCard from '@/components/layout/CreatorCard';
import Loader from '@/components/ui/Loader';
import Navbar from '@/components/layout/Navbar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

interface Creator {
  id: string;
  name: string;
  avatar: string;
  subscribers: number;
  isSubscribed: boolean;
}

// Types de tri disponibles
type SortType = 'subscribers' | 'recent' | 'alphabetical';

const Creators = () => {
  const { creators, subscribedCreators, loading, toggleSubscription, refetch, apiQuotaExceeded, searchQuotaExceeded, searchCreators, updateGenericCreators } = useCreators();
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogSearchTerm, setDialogSearchTerm] = useState('');
  const [mounted, setMounted] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Creator[]>([]);
  const [sortType, setSortType] = useState<SortType>('subscribers');
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  // Animation d'entrée progressive
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Filter creators based on search term - ONLY FILTERING SUBSCRIBED CREATORS
  const filteredCreators = subscribedCreators.filter(creator => 
    creator.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort creators based on selected sort type
  const sortedCreators = [...filteredCreators].sort((a, b) => {
    switch (sortType) {
      case 'subscribers':
        return b.subscribers - a.subscribers;
      case 'alphabetical':
        return a.name.localeCompare(b.name);
      case 'recent':
        // For this demo, we'll just return the reverse of the default order
        // In a real app, you would track when the user subscribed
        return a.subscribers - b.subscribers;
      default:
        return 0;
    }
  });

  // YouTube creator search
  const handleSearchYouTubeCreators = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchCreators(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching YouTube:', error);
      toast({
        title: "Erreur de recherche",
        description: "Impossible de rechercher des créateurs sur YouTube pour le moment.",
        variant: "destructive"
      });
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Search when search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isDialogOpen && dialogSearchTerm.trim()) {
        handleSearchYouTubeCreators(dialogSearchTerm);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [dialogSearchTerm, isDialogOpen]);

  // Sort creators by popularity (subscriber count)
  const suggestedCreators = [...creators].sort((a, b) => b.subscribers - a.subscribers).slice(0, 6);
  
  // Reset search when dialog opens
  const handleOpenDialog = () => {
    setDialogSearchTerm('');
    setSearchResults([]);
    setIsDialogOpen(true);
  };

  // Handle subscription
  const handleSubscribe = async (creatorId: string) => {
    try {
      await toggleSubscription(creatorId);
      
      // Update search results state
      if (searchResults.length > 0) {
        setSearchResults(prevResults => 
          prevResults.map(creator => {
            if (creator.id === creatorId) {
              return {
                ...creator,
                isSubscribed: !creator.isSubscribed
              };
            }
            return creator;
          })
        );
      }
      
      // Force refresh to update UI state
      await refetch();
    } catch (error) {
      console.error('Error toggling subscription:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la modification de l'abonnement.",
        variant: "destructive"
      });
    }
  };

  // Handle sort change
  const handleSortChange = (type: SortType) => {
    setSortType(type);
  };
  
  return (
    <div className="min-h-screen bg-youtube-light dark:bg-youtube-dark">
      <Navbar />
      
      <main className="page-container">
        {/* Main page header with search */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <h1 className="text-2xl font-bold flex items-center">
            <Users className="w-6 h-6 mr-2 text-youtube-red" />
            Mes créateurs
          </h1>
          
          <div className="flex items-center gap-2 w-full md:w-auto max-w-md relative">
            <Search className="absolute left-3 text-gray-400 pointer-events-none w-5 h-5" />
            <Input
              type="text"
              placeholder="Rechercher dans mes abonnements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded-full bg-gray-100 dark:bg-gray-800 border-none pl-10 pr-4 focus-visible:ring-youtube-red transition-all duration-300"
            />
            <Button 
              variant="ghost" 
              size="icon" 
              className="hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-transform duration-300 hover:scale-110"
              onClick={updateGenericCreators}
              title="Mettre à jour les créateurs"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-transform duration-300 hover:scale-110"
                >
                  <Filter className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleSortChange('subscribers')}>
                  <span className={sortType === 'subscribers' ? 'font-bold' : ''}>Trier par abonnés</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSortChange('alphabetical')}>
                  <span className={sortType === 'alphabetical' ? 'font-bold' : ''}>Trier par nom</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSortChange('recent')}>
                  <span className={sortType === 'recent' ? 'font-bold' : ''}>Trier par récents</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button 
              variant="ghost" 
              size="icon" 
              className="hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-transform duration-300 hover:scale-110"
              onClick={handleOpenDialog}
            >
              <Plus className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* API Quota Warning - Show if API quota is exceeded */}
        {apiQuotaExceeded && (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-4 mb-6 animate-fade-in">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-800 dark:text-amber-300">Limite d'API YouTube atteinte</h3>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  Certaines fonctionnalités peuvent être limitées. Les données affichées peuvent ne pas être à jour.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Creators grid or empty state */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader size="large" />
          </div>
        ) : sortedCreators.length > 0 ? (
          <div className="creator-grid">
            <div 
              className="bg-white dark:bg-youtube-dark border border-gray-200 dark:border-gray-800 rounded-xl p-4 transition-all duration-300 hover:shadow-lg flex flex-col items-center justify-center text-center min-h-[260px] hover:scale-[1.03] animate-fade-in cursor-pointer" 
              style={{ animationDelay: '0ms' }}
              onClick={handleOpenDialog}
            >
              <div className="w-24 h-24 mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center transform transition-transform duration-500 hover:scale-110">
                <Plus className="w-10 h-10 text-gray-500" />
              </div>
              <h3 className="font-medium mb-1">Ajouter</h3>
              <p className="text-youtube-gray-dark text-sm mb-4">
                Découvrir plus de créateurs
              </p>
            </div>
            
            {sortedCreators.map((creator, index) => (
              <div 
                key={creator.id} 
                className="transition-all duration-300 animate-fade-in"
                style={{ animationDelay: `${(index + 1) * 50}ms` }}
              >
                <CreatorCard 
                  {...creator} 
                  onSubscribe={handleSubscribe} 
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 animate-fade-in">
            <div className="mx-auto w-20 h-20 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
              <Search className="w-10 h-10 text-youtube-red" />
            </div>
            <h2 className="text-xl font-medium mb-4">Aucun abonnement</h2>
            <p className="text-gray-500 dark:text-gray-400">
              Vous n'êtes abonné à aucun créateur pour le moment.
            </p>
            <Button 
              onClick={handleOpenDialog}
              className="mt-4 bg-youtube-red hover:bg-red-600 text-white"
            >
              Découvrir des créateurs
            </Button>
          </div>
        )}
      </main>

      {/* Dialog pour découvrir plus de créateurs */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden bg-white dark:bg-youtube-dark">
          <DialogHeader className="p-6 pb-2">
            <div className="flex items-start justify-between mb-1">
              <div>
                <DialogTitle className="text-xl font-bold">Découvrir des créateurs</DialogTitle>
                <DialogDescription className="text-youtube-gray-dark mt-1">
                  Explorez notre sélection de créateurs populaires ou recherchez sur YouTube
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="p-6 pt-2">
            {/* API Quota Warning in dialog */}
            {apiQuotaExceeded && (
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-amber-800 dark:text-amber-300 text-sm">Limite d'API YouTube atteinte</h3>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                      La recherche de créateurs peut être limitée en ce moment.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none w-5 h-5" />
              <Input
                type="text"
                placeholder="Rechercher des créateurs sur YouTube..."
                value={dialogSearchTerm}
                onChange={(e) => setDialogSearchTerm(e.target.value)}
                className="pl-10 rounded-full bg-white dark:bg-gray-800 focus-visible:ring-youtube-red"
              />
            </div>

            {isSearching ? (
              <div className="flex justify-center items-center py-10">
                <Loader size="default" />
              </div>
            ) : searchQuotaExceeded && dialogSearchTerm ? (
              <div className="text-center py-8 px-4">
                <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
                <h3 className="font-medium text-lg mb-1">Recherche non disponible</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  La limite d'API YouTube a été atteinte. La recherche n'est pas disponible actuellement.
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Réessayez plus tard ou explorez nos suggestions ci-dessous.
                </p>
              </div>
            ) : dialogSearchTerm && searchResults.length > 0 ? (
              <div>
                <h3 className="font-medium text-lg mb-3">Résultats de recherche</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[350px] overflow-y-auto pr-2">
                  {searchResults.map((creator) => (
                    <div 
                      key={creator.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <img 
                        src={creator.avatar} 
                        alt={creator.name} 
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm">
                          <span className="block truncate max-w-[180px]">{creator.name}</span>
                        </h4>
                        <p className="text-xs text-youtube-gray-dark truncate max-w-[180px]">
                          {creator.subscribers.toLocaleString('fr-FR')} abonnés
                        </p>
                      </div>
                      <Button 
                        onClick={() => handleSubscribe(creator.id)}
                        className={`flex-shrink-0 ${creator.isSubscribed ? 'bg-gray-200 dark:bg-gray-700 text-black dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600' : 'bg-youtube-red text-white hover:bg-red-600'}`}
                        size="sm"
                      >
                        {creator.isSubscribed ? 'Abonné' : 'S\'abonner'}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : dialogSearchTerm && !isSearching && !searchQuotaExceeded ? (
              <div className="text-center py-8">
                <p>Aucun résultat pour "{dialogSearchTerm}"</p>
              </div>
            ) : (
              <>
                <h3 className="font-medium text-lg mb-3">Suggestions pour vous</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {suggestedCreators.map((creator) => (
                    <div 
                      key={creator.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <img 
                        src={creator.avatar} 
                        alt={creator.name} 
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm">
                          <span className="block truncate max-w-[180px]">{creator.name}</span>
                        </h4>
                        <p className="text-xs text-youtube-gray-dark truncate max-w-[180px]">
                          {creator.subscribers.toLocaleString('fr-FR')} abonnés
                        </p>
                      </div>
                      <Button 
                        onClick={() => handleSubscribe(creator.id)}
                        className={`flex-shrink-0 ${creator.isSubscribed ? 'bg-gray-200 dark:bg-gray-700 text-black dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600' : 'bg-youtube-red text-white hover:bg-red-600'}`}
                        size="sm"
                      >
                        {creator.isSubscribed ? 'Abonné' : 'S\'abonner'}
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Creators;
